<script setup lang="ts">
import type { Schemas } from "#shopware";

const props = defineProps<{
  product: Schemas["Product"];
  quantity: number;
  hidePrice?: boolean;
}>();

const { quantity } = toRefs(props);

const emits = defineEmits<{
  (
    e: "select",
    product: {
      id: string | undefined;
      quantity: number;
      cover: Schemas["Media"] | undefined;
      productNumber: string;
      name: string;
      unitPrice: number | undefined;
      type: string;
    }
  ): void;
  (e: "remove", id: string): void;
}>();

const { product } = toRefs(props);

const selected = ref(false);

const selectProduct = () => {
  const isProductSelected = selected.value;
  const productId = selectedVariant.value?.id;
  const payload = {
    id: productId,
    quantity: quantity.value,
    cover: product.value?.cover?.media,
    name: product.value?.translated?.name,
    unitPrice: getProductPrice.value,
    productNumber: selectedVariant.value?.productNumber as string,
    type: "product",
  };

  if (isProductSelected) {
    emits("select", payload);
  } else {
    emits("remove", productId as string);
  }
};

const addProduct = () => {
  selected.value = true;
  selectProduct();
};

const removeProduct = () => {
  if (!selected.value) return;
  selected.value = false;
  selectProduct();
};

const { apiClient } = useShopwareContext();

const selectedVariant = ref<Schemas["Product"] | undefined>();
const selectedOption = ref<string | undefined>("");

const { data: productResponse } = await apiClient.invoke(
  "readProductDetail post /product/{productId}",
  {
    pathParams: { productId: product.value?.id },
    body: {
      includes: {
        product: ["id", "children"],
      },
      associations: {
        children: {},
      },
    },
  }
);

const variants = computed(() => {
  return productResponse?.configurator?.[0]?.options;
});

const loading = ref(false);
const { unitPrice, tierPrices } = useProductPrice(selectedVariant);

const getProductPrice = computed(() => {
  const tierPrice = tierPrices.value.find(
    (price) => price.quantity >= quantity.value
  );
  return tierPrice?.unitPrice || unitPrice.value || 0;
});

const fetchProductPrice = async (optionId: string) => {
  loading.value = true;
  const { data } = await apiClient.invoke("readProduct post /product", {
    body: {
      filter: [
        {
          type: "equals",
          field: "parentId",
          value: product.value?.parentId as string,
        },
        {
          type: "equals",
          field: "optionIds",
          value: optionId,
        },
      ],
      includes: {
        product: ["id", "calculatedPrices", "productNumber"],
      },
    },
  });

  selectedVariant.value = data?.elements?.[0];

  loading.value = false;
};

watch(
  () => variants.value,
  (newVariants) => {
    selectedOption.value =
      newVariants?.[0]?.id ||
      productResponse?.configurator?.[0]?.options?.[0]?.id;
  },
  { immediate: true }
);

watch(
  () => selectedOption.value,
  (option) => {
    if (option) {
      fetchProductPrice(option);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  selected.value = false;
});
</script>
<template>
  <div class="p-5">
    <div class="w-[12.75rem] h-[12.75rem] mx-auto mb-5">
      <NuxtImg
        format="webp"
        loading="lazy"
        :src="product?.cover?.media?.url"
        :alt="product?.translated?.name"
        :width="product?.cover?.media?.metaData?.width"
        :height="product?.cover?.media?.metaData?.height"
        class="object-contain h-full w-full"
      />
    </div>
    <p class="text-sm !mb-3">
      {{ $t("product.productNumber") }}: {{ product?.productNumber }}
    </p>
    <h6 class="font-bold text-base mb-3">{{ product?.translated?.name }}</h6>
    <h6 class="font-bold text-sm mb-3">
      <SharedPrice
        v-if="!hidePrice"
        :value="getProductPrice * quantity"
        class="font-bold text-base"
      />
    </h6>
    <ul class="flex items-center justify-center flex-wrap gap-2.5 mb-5">
      <template v-for="variant in variants">
        <li class="relative">
          <input
            type="radio"
            :name="`cleaner_${product?.id}`"
            v-model="selectedOption"
            :value="variant.id"
            class="[&:checked+label]:border-skin-theme absolute w-full h-full cursor-pointer left-0 top-0 opacity-0"
            @change="removeProduct"
          />
          <label
            for=""
            class="block w-full h-11 px-2 rounded-full bg-skin-icon uppercase flex items-center justify-center text-xxs font-bold border-2 border-[transparent]"
          >
            <b class="inline-block bg-no-repeat bg-[center] leading-8">{{
              variant.name
            }}</b>
            <span v-if="selected && selectedOption === variant.id" class="ml-2"
              ><i class="fa-solid fa-check"></i
            ></span>
          </label>
        </li>
      </template>
    </ul>
    <div>
      <button
        type="button"
        :disabled="selected || loading"
        :class="{
          'opacity-50 cursor-not-allowed': selected || loading,
        }"
        @click="addProduct"
        class="btn-outline !py-2"
      >
        {{
          selected
            ? $t("product.configurator.add")
            : $t("product.configurator.added")
        }}
      </button>
    </div>
  </div>
</template>
